import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import axios from 'axios'


// Optional neighbor expansion logic
function getNeighbors(beats, selectedId) {
  const index = beats.findIndex(b => b.id === selectedId)
  if (index === -1) return []
  const neighbors = []
  if (index > 0) neighbors.push(beats[index - 1])
  neighbors.push(beats[index])
  if (index < beats.length - 1) neighbors.push(beats[index + 1])
  return neighbors
}

export default function ScenePage({ 
    sceneTitle, 
    dramaticQuestion, 
    beats, 
    movieHash,        // <- pass these via server props so we know what to call
    sceneNumber       // <- pass these via server props as well
  }) {
    // Track the ID of the currently-selected beat
  const [selectedBeatId, setSelectedBeatId] = useState(null)
  const [workspaceData] = useState('Some outline or workspace data here...')

  // Beat texts stored separately so we can edit them
  const [beatTexts, setBeatTexts] = useState(
    beats.reduce((acc, beat) => {
      acc[beat.id] = beat.text
      return acc
    }, {})
  )

  // Temporary edit buffer for whichever beat is selected
  const [editBuffer, setEditBuffer] = useState('')

  // If you want neighbor expansion, use getNeighbors:
  const expandedBeats = selectedBeatId ? getNeighbors(beats, selectedBeatId) : []

  // When clicking a beat row
  const handleBeatClick = (beatId) => {
    if (beatId === selectedBeatId) {
      // Deselect
      setSelectedBeatId(null)
      setEditBuffer('')
    } else {
      setSelectedBeatId(beatId)
      setEditBuffer(beatTexts[beatId])
    }
  }

  // Handle typing in the textarea for the selected beat
  const handleEditChange = (e) => {
    setEditBuffer(e.target.value)
  }

  // Save changes to beat’s text
  const handleSave = (beatId) => {
    setBeatTexts(prev => ({
      ...prev,
      [beatId]: editBuffer
    }))
  }

  return (
    <div className="container my-4">
      {/* Scene Header */}
      <div className="mb-3 text-center">
        <h1>{sceneTitle}</h1>
        {dramaticQuestion && (
          <h5 className="text-muted">
            Dramatic Question: {dramaticQuestion}
          </h5>
        )}
      </div>

      <div className="row">
        {/* Column for Beats (the "table" for columns 1 & 2) */}
        <div className="col-md-8">
          <table className="table table-bordered align-middle">
            <thead>
              <tr className="table-light">
                <th style={{ width: '30%' }}>Summary / States</th>
                <th style={{ width: '70%' }}>Beat Text</th>
              </tr>
            </thead>
            <tbody>
              {beats.map(beat => {
                const isSelected = beat.id === selectedBeatId
                // If neighbor expansion, check if beat is in the expanded group
                const inExpandedGroup = expandedBeats.some(b => b.id === beat.id)
                const snippet = beatTexts[beat.id].split(' ').slice(0, 8).join(' ')

                return (
                  <tr
                    key={beat.id}
                    className={isSelected ? 'table-active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleBeatClick(beat.id)}
                  >
                    {/* LEFT COLUMN: summary & states */}
                    <td>
                      <div className="fw-bold mb-1">
                        <i className="fas fa-angle-right me-2"></i>
                        {beat.summary}
                      </div>
                      <small className="text-muted">
                        <strong>Begin:</strong> {beat.stateAtBeginning} |{' '}
                        <strong>End:</strong> {beat.stateAtEnd}
                      </small>
                    </td>

                    {/* RIGHT COLUMN: text (snippet, expanded, or editable) */}
                    <td>
                      {/* If this beat is in the expanded group and is selected, show textarea */}
                      {inExpandedGroup && isSelected ? (
                        <>
                          <textarea
                            className="form-control"
                            rows="4"
                            value={editBuffer}
                            onChange={handleEditChange}
                          />
                          <button
                            className="btn btn-sm btn-primary mt-2"
                            onClick={() => handleSave(beat.id)}
                          >
                            Save
                          </button>
                        </>
                      ) : inExpandedGroup ? (
                        // If expanded but NOT the selected row, just show full text
                        <p className="mb-0">{beatTexts[beat.id]}</p>
                      ) : (
                        // If not expanded, show a snippet
                        <p className="mb-0">{snippet} ...</p>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Column 3: Chat */}
        <ChatColumn
        selectedBeatId={selectedBeatId}
        beats={beats}
        beatTexts={beatTexts}
        workspaceData={workspaceData}
        movieHash={movieHash}
        sceneNumber={sceneNumber}
        onReplaceText={(id, newText) => {
          setBeatTexts(prev => ({ ...prev, [id]: newText }))
        }}
      />
      </div>
    </div>
  )
}

function ChatColumn({ 
    selectedBeatId, 
    beats, 
    beatTexts, 
    movieHash, 
    sceneNumber, 
    onReplaceText 
  }) {
    const [chatLog, setChatLog] = useState([
      { user: 'System', text: 'Welcome! Select a beat to begin.' }
    ])
    const [userMessage, setUserMessage] = useState('')
    const [threadId, setThreadId] = useState(null)


    const chatTitle = selectedBeatId
      ? `Chat about Beat #${selectedBeatId}`
      : 'Chat'
  
    // We'll use a single function to gather data + question and send it via Inertia
    const sendToChatRoute = (question) => {
      // Bundle up the entire page's data
      const payload = {
        selectedBeatId,
        beats,
        beatTexts,
        question
      }
  
      // Post to your route: "screenplay.chat"
      router.post(
        route('screenplay.chat', { movie: movieHash, scene_number: sceneNumber }),
        payload, // data to send
        {
          // Optional Inertia settings
          preserveScroll: true,
          onSuccess: (page) => {
            // The "page" here is the updated Inertia page object.
            // If your controller returns a JSON or new props, handle them here
            console.log('Chat response', page)
            // If you get new chat messages from the server, you could update chatLog, etc.
          }
        }
      )
    }
  
    const handleSend = async (e) => {
        e.preventDefault()
        if (!userMessage.trim()) return
    
        // 1) Add user’s message to the local chat log
        setChatLog(prev => [...prev, { user: 'You', text: userMessage }])
    
        // 2) Prepare the data for the server
        const payload = {
          thread_id: threadId,         // if null, server will create a new thread
          selectedBeatId,
          beats,
          beatTexts,
          question: userMessage        // user’s typed input
        }
    
            try {
                // 3) Post to your new route
                const url = `/screenplay/${movieHash}/${sceneNumber}/chat`
                const response = await axios.post(url, payload)
          
                // 4) Check server response
                const data = response.data
                if (data.thread_id) {
                  setThreadId(data.thread_id)
                }
                if (data.chat_response) {
                  setChatLog(prev => [...prev, { user: 'AI', text: data.chat_response }])
                } else if (data.error) {
                  // e.g., server might respond with { error: "Failed to process..." }
                  setChatLog(prev => [...prev, { user: 'System', text: data.error }])
                }
              } catch (err) {
                console.error('Chat error', err)
                setChatLog(prev => [...prev, {
                  user: 'System',
                  text: 'An error occurred while sending your message.'
                }])
              }
          
              // 5) Clear user message
              setUserMessage('')
            }
  
    const handleOptionClick = (optionText) => {
      if (!selectedBeatId) return
      // Replace local text
      onReplaceText(selectedBeatId, optionText)
      // For demonstration, just add to local chat. 
      // If you also want to send that to the server, call sendToChatRoute(optionText).
      setChatLog(prev => [
        ...prev,
        { user: 'System', text: `Replaced beat #${selectedBeatId} with: "${optionText}"` }
      ])
    }

    return (
        <div className="col-md-4" style={{ backgroundColor: selectedBeatId ? '#f8f9fa' : 'white' }}>
          <div className="card">
            <div className="card-header">
              <i className="fas fa-comments me-2" />
              {chatTitle}
            </div>
            <div className="card-body" style={{ height: '300px', overflowY: 'auto' }}>
              {chatLog.map((msg, index) => (
                <div key={index} className="mb-2">
                  <strong>{msg.user}:</strong> {msg.text}
                  {/* If the message has "options", we render them with clickable badges, etc. */}
                  {msg.options && (
                    <div className="mt-1">
                      {msg.options.map((opt, i) => (
                        <span
                          key={i}
                          className="badge bg-secondary me-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleOptionClick(opt)}
                        >
                          {opt}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="card-footer">
              <form onSubmit={handleSend} className="d-flex">
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="Ask about the scene or beat..."
                  value={userMessage}
                  onChange={e => setUserMessage(e.target.value)}
                />
                <button className="btn btn-primary">Send</button>
              </form>
            </div>
          </div>
        </div>
      )
    }
